import React, { createContext, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { ref, get, set, update } from 'firebase/database';
import { auth, database } from '../firebase/firebase';

export type RoleKey = 'admin' | 'editor' | 'staff';

export interface UserRoles {
  admin: boolean;
  editor: boolean;
  staff: boolean;
}

export interface AppUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  roles: UserRoles;
  date_created?: string;   // ISO
  last_logged_in?: string; // ISO
}

interface AuthContextValue {
  authUser: AppUser | null;
  loading: boolean;
  isAdmin: boolean;
  isEditor: boolean;
  isStaff: boolean;
  userSignOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue>({
  authUser: null,
  loading: true,
  isAdmin: false,
  isEditor: false,
  isStaff: false,
  userSignOut: async () => {},
});

const DEFAULT_ROLES: UserRoles = { admin: false, editor: false, staff: false };

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [authUser, setAuthUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      try {
        if (!fbUser) {
          setAuthUser(null);
          setLoading(false);
          return;
        }

        const userRef = ref(database, `users/${fbUser.uid}`);
        const snap = await get(userRef);

        const nowISO = new Date().toISOString();
        if (!snap.exists()) {
          // New record with all roles false
          const record: AppUser = {
            uid: fbUser.uid,
            email: fbUser.email,
            displayName: fbUser.displayName ?? '',
            roles: DEFAULT_ROLES,
            date_created: nowISO,
            last_logged_in: nowISO,
          };
          await set(userRef, record);
          setAuthUser(record);
        } else {
          // Merge roles and update last login
          const data = snap.val() as Partial<AppUser>;
          const merged: AppUser = {
            uid: fbUser.uid,
            email: fbUser.email ?? data.email ?? null,
            displayName: fbUser.displayName ?? (data.displayName ?? null),
            roles: {
              admin: !!data?.roles?.admin,
              editor: !!data?.roles?.editor,
              staff: !!data?.roles?.staff,
            },
            date_created: data.date_created,
            last_logged_in: nowISO,
          };
          await update(userRef, { last_logged_in: nowISO, email: merged.email, displayName: merged.displayName });
          setAuthUser(merged);
        }
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
  }, []);

  const userSignOut = async () => { await signOut(auth); };

  const value = useMemo((): AuthContextValue => ({
    authUser,
    loading,
    isAdmin: !!authUser?.roles.admin,
    isEditor: !!authUser?.roles.editor,
    isStaff:  !!authUser?.roles.staff,
    userSignOut,
  }), [authUser, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
